import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  userPartyAssignmentService,
  PartyAssignmentConflictError,
  type UserPartyAssignmentListParams,
  type UserPartyAssignmentListResponse,
  type UserPartyAssignmentCreateRequest,
  type UserPartyAssignmentUpdateRequest,
} from '../../services/api/userPartyAssignmentService';

export const loadUserPartyAssignments = createAsyncThunk<
  UserPartyAssignmentListResponse,
  UserPartyAssignmentListParams
>(
  'userPartyAssignment/load',
  async (params, { rejectWithValue }) => {
    try {
      return await userPartyAssignmentService.list(params);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to load assignments');
    }
  }
);

export const createUserPartyAssignment = createAsyncThunk<
  { assignmentId: number },
  { tenantId: number; request: UserPartyAssignmentCreateRequest }
>(
  'userPartyAssignment/create',
  async ({ tenantId, request }, { rejectWithValue }) => {
    try {
      return await userPartyAssignmentService.create(tenantId, request);
    } catch (error: unknown) {
      if (error instanceof PartyAssignmentConflictError) {
        return rejectWithValue({ reasonCode: error.reasonCode, message: error.message });
      }
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to create assignment');
    }
  }
);

export const updateUserPartyAssignment = createAsyncThunk<
  { assignmentId: number },
  { tenantId: number; id: number; request: UserPartyAssignmentUpdateRequest }
>(
  'userPartyAssignment/update',
  async ({ tenantId, id, request }, { rejectWithValue }) => {
    try {
      return await userPartyAssignmentService.update(tenantId, id, request);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update assignment');
    }
  }
);

export const deleteUserPartyAssignment = createAsyncThunk<
  { assignmentId: number },
  { tenantId: number; id: number }
>(
  'userPartyAssignment/delete',
  async ({ tenantId, id }, { rejectWithValue }) => {
    try {
      return await userPartyAssignmentService.delete(tenantId, id);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete assignment');
    }
  }
);

/**
 * PATCH a user's affiliation_type. Lives in this thunks file (rather than
 * userThunks) because the endpoint is gated on the EDI permission and is
 * dispatched from EDI Admin / user-edit modal — same domain.
 */
export const updateUserAffiliation = createAsyncThunk<
  { affiliationType: string },
  { userId: number; affiliationType: string }
>(
  'userPartyAssignment/updateUserAffiliation',
  async ({ userId, affiliationType }, { rejectWithValue }) => {
    try {
      return await userPartyAssignmentService.updateUserAffiliation(userId, affiliationType);
    } catch (error: unknown) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update affiliation');
    }
  }
);
